import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@shared/ui/command';
import { BottomSheet } from '@shared/ui/adaptive/BottomSheet';
import { useMobileShell } from '@shared/shell';
import { allSuites } from '@suites/registry';
import { useAuth } from '@shared/auth/AuthContext';
import { useRecentPages } from '@shared/activity';
import { SuiteIcon } from '@shared/icons';

export const MobileSearchDialog: React.FC = () => {
  const { searchOpen, closeSearch } = useMobileShell();
  const navigate = useNavigate();
  const { hasPermission, enabledSuites } = useAuth();
  const recentPages = useRecentPages();

  const handleSelect = (path: string) => {
    closeSearch();
    navigate(path);
  };

  const permittedSuites = allSuites.filter((s) => {
    if (enabledSuites && enabledSuites.length > 0 && !enabledSuites.includes(s.id)) return false;
    return s.requiredPermission ? hasPermission(s.requiredPermission) : true;
  });

  return (
    <BottomSheet
      open={searchOpen}
      onOpenChange={(open) => {
        if (!open) closeSearch();
      }}
      title="Search Platform"
      className="max-h-[92dvh] p-0"
    >
      <Command className="rounded-lg border-none bg-transparent">
        <CommandInput placeholder="Search suites, pages, actions..." autoFocus />
        <CommandList className="max-h-[65dvh] px-2 py-1">
          <CommandEmpty>No matching results.</CommandEmpty>

          {recentPages.length > 0 && (
            <CommandGroup heading="Recent Pages">
              {recentPages.slice(0, 5).map((page) => (
                <CommandItem
                  key={page.key}
                  onSelect={() => handleSelect(page.path)}
                  className="py-2.5"
                >
                  <SuiteIcon name={page.icon} className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{page.title}</span>
                  <span className="ml-auto text-xs text-muted-foreground font-mono">
                    {page.path}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          <CommandSeparator />

          <CommandGroup heading="Suites">
            {permittedSuites.map((suite) => (
              <CommandItem
                key={suite.id}
                onSelect={() => handleSelect(suite.basePath)}
                className="py-2.5"
              >
                <SuiteIcon name={suite.icon} className="mr-2 h-4 w-4 text-primary" />
                <span className="text-sm font-medium">{suite.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandSeparator />

          <CommandGroup heading="Pages">
            {permittedSuites.flatMap((suite) =>
              suite.nav
                .filter((nav) => (nav.permission ? hasPermission(nav.permission) : true))
                .map((nav) => (
                  <CommandItem
                    key={`${suite.id}:${nav.path}`}
                    onSelect={() => handleSelect(nav.path)}
                    className="py-2.5"
                  >
                    <SuiteIcon name={nav.icon} className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{nav.label}</span>
                    <span className="ml-auto text-xs text-muted-foreground">{suite.name}</span>
                  </CommandItem>
                ))
            )}
          </CommandGroup>
        </CommandList>
      </Command>
    </BottomSheet>
  );
};

export default MobileSearchDialog;
